import React from "react";
import { useColors } from "../constants/Colors";
import {
  Home,
  Bookmark,
  Send,
  Inbox,
  Search,
  MessageSquare,
  User,
  LucideIcon,
} from "lucide-react";

export type EmptyStateType =
  | "no-flats"
  | "no-saved"
  | "no-sent-applications"
  | "no-received-applications"
  | "no-results"
  | "no-chats"
  | "no-profile";

interface EmptyStateProps {
  type: EmptyStateType;
  /** Optional custom title override */
  title?: string;
  /** Optional custom description override */
  description?: string;
  /** Optional action button */
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface EmptyStateConfig {
  icon: LucideIcon;
  title: string;
  description: string;
  gradient: [string, string];
}

// Shared gradient color for all empty states
const sharedGradient: [string, string] = ["#6366f1", "#8b5cf6"];

const emptyStateConfigs: Record<EmptyStateType, EmptyStateConfig> = {
  "no-flats": {
    icon: Home,
    title: "No flats yet",
    description: "Create your first flat listing to start finding flatmates.",
    gradient: sharedGradient,
  },
  "no-saved": {
    icon: Bookmark,
    title: "No saved flats",
    description: "Save flats you're interested in to view them here later.",
    gradient: sharedGradient,
  },
  "no-sent-applications": {
    icon: Send,
    title: "No applications sent",
    description: "Apply to flats you're interested in to start your search.",
    gradient: sharedGradient,
  },
  "no-received-applications": {
    icon: Inbox,
    title: "No applications received",
    description: "Applications from potential flatmates will appear here.",
    gradient: sharedGradient,
  },
  "no-results": {
    icon: Search,
    title: "No results found",
    description: "Try adjusting your filters or search in a different area.",
    gradient: sharedGradient,
  },
  "no-chats": {
    icon: MessageSquare,
    title: "No conversations yet",
    description: "Start chatting with flatmates or flat seekers.",
    gradient: sharedGradient,
  },
  "no-profile": {
    icon: User,
    title: "Your Profile",
    description: "Sign in to view and manage your profile.",
    gradient: sharedGradient,
  },
};

const EmptyState: React.FC<EmptyStateProps> = ({
  type,
  title: customTitle,
  description: customDescription,
  action,
}) => {
  const colors = useColors();
  const config = emptyStateConfigs[type];
  const Icon = config.icon;

  const title = customTitle || config.title;
  const description = customDescription || config.description;

  return (
    <div className="w-full max-w-full flex-1 flex flex-col items-center justify-center py-16 px-6 overflow-hidden">
      {/* Icon Container */}
      <div
        className="relative w-28 h-28 mb-6 rounded-3xl flex items-center justify-center mx-auto overflow-visible"
        style={{
          background: `linear-gradient(135deg, ${config.gradient[0]}15, ${config.gradient[1]}25)`,
        }}
      >
        {/* Background decorative circles */}
        <div
          className="absolute -top-2 -right-2 w-8 h-8 rounded-full opacity-40"
          style={{
            background: `linear-gradient(135deg, ${config.gradient[0]}, ${config.gradient[1]})`,
          }}
        />
        <div
          className="absolute -bottom-1 -left-1 w-5 h-5 rounded-full opacity-30"
          style={{
            background: `linear-gradient(135deg, ${config.gradient[0]}, ${config.gradient[1]})`,
          }}
        />

        {/* Icon */}
        <Icon
          size={48}
          strokeWidth={1.5}
          style={{
            color: config.gradient[0],
          }}
        />
      </div>

      {/* Title */}
      <h3
        className="text-xl font-semibold mb-2 text-center"
        style={{ color: colors.textNeutralDefault }}
      >
        {title}
      </h3>

      {/* Description */}
      <p
        className="text-base max-w-xs leading-relaxed text-center mx-auto break-words"
        style={{ color: colors.textNeutralMuted }}
      >
        {description}
      </p>

      {/* Optional Action Button */}
      {action && (
        <button
          onClick={action.onClick}
          className="mt-6 px-6 py-3 rounded-full font-semibold transition-all hover:opacity-90 hover:scale-105 mx-auto"
          style={{
            background: `linear-gradient(135deg, ${config.gradient[0]}, ${config.gradient[1]})`,
            color: "white",
          }}
        >
          {action.label}
        </button>
      )}
    </div>
  );
};

export default EmptyState;
